// ---------------------------------------------------------------------------
// CLIENT — Gemini Live WebSocket session manager.
//
// Handles: mic capture (16kHz PCM), WS to /api/voice/live, audio playback
// (24kHz PCM), barge-in, and state callbacks (idle/speaking/interrupted).
//
// Usage:
//   const live = new LiveSession({ onState, onUserTranscript, onAgentTranscript });
//   await live.start({ userId, mode: 'casual' });
//   live.toggleMute();
//   live.stop();
//
// Wire protocol client → server:
//   { type:'audio', data:<base64-pcm16-16k>, mime:'audio/pcm;rate=16000' }
//   { type:'audio_stream_end' }
// Server → client: session_started, setup_sent, blocked, session_closed,
//   error, plus Gemini serverContent passthroughs.
// ---------------------------------------------------------------------------

function pcm16ToBase64(float32) {
  const pcm = new Int16Array(float32.length);
  for (let i = 0; i < float32.length; i++) {
    const v = Math.max(-1, Math.min(1, float32[i]));
    pcm[i] = v < 0 ? v * 0x8000 : v * 0x7fff;
  }
  const bytes = new Uint8Array(pcm.buffer);
  let binary = "";
  for (let i = 0; i < bytes.length; i++) binary += String.fromCharCode(bytes[i]);
  return btoa(binary);
}

function base64ToInt16(b64) {
  const bin = atob(b64);
  const len = bin.length;
  const bytes = new Uint8Array(len);
  for (let i = 0; i < len; i++) bytes[i] = bin.charCodeAt(i);
  return new Int16Array(bytes.buffer);
}

export class LiveSession {
  constructor({ backendUrl, onState, onUserTranscript, onAgentTranscript, onSponsoredCards, onError, onClose } = {}) {
    const base = backendUrl || (process.env.REACT_APP_BACKEND_URL || window.location.origin);
    // Derive ws:// or wss:// from REACT_APP_BACKEND_URL.
    this.wsUrl = base.replace(/^http/i, "ws") + "/api/voice/live";
    this.onState = onState || (() => {});
    this.onUserTranscript = onUserTranscript || (() => {});
    this.onAgentTranscript = onAgentTranscript || (() => {});
    this.onSponsoredCards = onSponsoredCards || (() => {});
    this.onError = onError || (() => {});
    this.onClose = onClose || (() => {});
    this.ws = null;
    this.audioCtx = null;
    this.outCtx = null;
    this.source = null;
    this.processor = null;
    this.stream = null;
    this.muted = false;
    this.outQueue = []; // pending AudioBufferSourceNodes
    this.nextPlayTime = 0;
    this.state = "idle"; // idle | listening | speaking | interrupted | reconnecting | closed
    this.sessionId = null;
    // v1.12.2 — client-side safety-net reconnect (ingress / network blips).
    this._userId = null;
    this._mode = "casual";
    this._stoppedByUser = false;
    this._reconnectAttempts = 0;
    this._reconnectMax = 3;
    this._reconnectBackoffMs = [1000, 3000, 5000];
    this._reconnectTimer = null;
  }

  _setState(s) {
    this.state = s;
    this.onState(s);
  }

  async start({ userId, mode = "casual" } = {}) {
    if (this.ws) return; // already started
    this._userId = userId;
    this._mode = mode;
    this._stoppedByUser = false;
    this._reconnectAttempts = 0;
    await this._openSocket({ keepAudio: false });
  }

  async _openSocket({ keepAudio = false } = {}) {
    const qs = new URLSearchParams({ mode: this._mode });
    if (this._userId) qs.set("userId", this._userId);
    this.ws = new WebSocket(`${this.wsUrl}?${qs.toString()}`);
    this.ws.onmessage = (ev) => this._onServerMessage(ev.data);
    this.ws.onerror = (e) => this.onError({ type: "ws_error", error: e });
    this.ws.onclose = (ev) => this._handleClientClose(ev);
    this.ws.onopen = () => {
      // Successful (re)connect resets the attempt counter.
      this._reconnectAttempts = 0;
    };
    if (!keepAudio) {
      // Acquire mic + build the 16kHz capture graph (first connect only).
      this.stream = await navigator.mediaDevices.getUserMedia({
        audio: { channelCount: 1, sampleRate: 16000, echoCancellation: true, noiseSuppression: true, autoGainControl: true },
      });
      this.audioCtx = new (window.AudioContext || window.webkitAudioContext)({ sampleRate: 16000 });
      this.source = this.audioCtx.createMediaStreamSource(this.stream);
      this.processor = this.audioCtx.createScriptProcessor(4096, 1, 1);
      this.processor.onaudioprocess = (e) => {
        if (this.muted || !this.ws || this.ws.readyState !== WebSocket.OPEN) return;
        const input = e.inputBuffer.getChannelData(0);
        const b64 = pcm16ToBase64(input);
        this.ws.send(JSON.stringify({ type: "audio", data: b64, mime: "audio/pcm;rate=16000" }));
      };
      this.source.connect(this.processor);
      this.processor.connect(this.audioCtx.destination);
      this.outCtx = new (window.AudioContext || window.webkitAudioContext)({ sampleRate: 24000 });
      this.nextPlayTime = this.outCtx.currentTime;
    }
    this._setState("listening");
  }

  _handleClientClose(ev) {
    const code = ev?.code ?? 1006;
    const reason = ev?.reason || "";
    // User-initiated stop → final close, no retry.
    if (this._stoppedByUser) {
      this._setState("closed");
      this.onClose({ code, reason });
      this._stopAudio();
      return;
    }
    // Code 1000 with no user request still happens when the backend gives up
    // (e.g., upstream cap hit) — treat as recoverable up to _reconnectMax.
    if (this._reconnectAttempts >= this._reconnectMax) {
      this._setState("closed");
      this.onClose({ code, reason, gave_up: true });
      this._stopAudio();
      return;
    }
    const delay = this._reconnectBackoffMs[Math.min(this._reconnectAttempts, this._reconnectBackoffMs.length - 1)];
    this._reconnectAttempts += 1;
    this._setState("reconnecting");
    if (this._reconnectTimer) clearTimeout(this._reconnectTimer);
    this._reconnectTimer = setTimeout(async () => {
      try {
        // Keep audio graphs alive across reconnects: only the WS is re-opened.
        await this._openSocket({ keepAudio: true });
      } catch (e) {
        this.onError({ type: "reconnect_failed", error: e, attempt: this._reconnectAttempts });
        // Try again until max attempts reached.
        this._handleClientClose({ code: 1006, reason: "reconnect_failed" });
      }
    }, delay);
  }

  toggleMute() { this.muted = !this.muted; return this.muted; }

  stop() {
    this._stoppedByUser = true;
    if (this._reconnectTimer) { clearTimeout(this._reconnectTimer); this._reconnectTimer = null; }
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      try { this.ws.send(JSON.stringify({ type: "close" })); } catch (_) {}
      try { this.ws.close(); } catch (_) {}
    }
    this._stopAudio();
    this._setState("closed");
  }

  _stopAudio() {
    try { this.processor && this.processor.disconnect(); } catch (_) {}
    try { this.source && this.source.disconnect(); } catch (_) {}
    try { this.stream && this.stream.getTracks().forEach((t) => t.stop()); } catch (_) {}
    try { this.audioCtx && this.audioCtx.close(); } catch (_) {}
    try { this.outCtx && this.outCtx.close(); } catch (_) {}
    this.ws = null;
    this.audioCtx = null;
    this.outCtx = null;
    this.source = null;
    this.processor = null;
    this.stream = null;
    this.outQueue = [];
  }

  _flushOutputQueue() {
    for (const src of this.outQueue) { try { src.stop(); } catch (_) {} }
    this.outQueue = [];
    this.nextPlayTime = this.outCtx ? this.outCtx.currentTime : 0;
  }

  _playPcmChunk(b64) {
    if (!this.outCtx) return;
    const int16 = base64ToInt16(b64);
    const float32 = new Float32Array(int16.length);
    for (let i = 0; i < int16.length; i++) float32[i] = int16[i] / 0x8000;
    const buf = this.outCtx.createBuffer(1, float32.length, 24000);
    buf.getChannelData(0).set(float32);
    const src = this.outCtx.createBufferSource();
    src.buffer = buf;
    src.connect(this.outCtx.destination);
    const startAt = Math.max(this.outCtx.currentTime + 0.02, this.nextPlayTime);
    src.start(startAt);
    this.nextPlayTime = startAt + buf.duration;
    this.outQueue.push(src);
    src.onended = () => { this.outQueue = this.outQueue.filter((s) => s !== src); };
  }

  _onServerMessage(raw) {
    let msg;
    try { msg = JSON.parse(raw); } catch (_) { return; }
    // 1) Our proxy meta.
    if (msg.type === "session_started") { this.sessionId = msg.session_id; return; }
    if (msg.type === "setup_sent") {
      // After a transparent rolling restart, the resumed flag tells us the
      // backend reused the previous handle. Quietly transition back to a
      // listening state if we were showing "reconnecting".
      if (msg.resumed && this.state === "reconnecting") {
        this._setState("listening");
      }
      return;
    }
    if (msg.type === "rolling_restart") {
      // Backend opened a fresh upstream behind the scenes. Do not surface a
      // hard error; just transition the indicator briefly so the UI can
      // show a soft "reconnecting" state. White-label: no provider name.
      this._setState("reconnecting");
      return;
    }
    if (msg.type === "blocked") { this._setState("interrupted"); this.onError({ type: "blocked", reason: msg.reason }); return; }
    if (msg.type === "session_closed") {
      // Informational: the ws.onclose handler decides whether to reconnect.
      return;
    }
    if (msg.type === "sponsored_cards_for_turn") {
      // v1.14.0 — partner cards pushed mid-session; the host decides where
      // to attach them in the chat history.
      try { this.onSponsoredCards(msg); } catch (_) {}
      return;
    }
    if (msg.type === "error") { this.onError(msg); return; }
    // 2) Gemini serverContent passthrough.
    const sc = msg.serverContent;
    if (!sc) return;
    if (sc.interrupted) {
      this._setState("interrupted");
      this._flushOutputQueue();
      setTimeout(() => this._setState("listening"), 400);
    }
    if (sc.inputTranscription?.text) this.onUserTranscript(sc.inputTranscription.text);
    if (sc.outputTranscription?.text) this.onAgentTranscript(sc.outputTranscription.text);
    const parts = sc.modelTurn?.parts || [];
    for (const p of parts) {
      if (p.inlineData?.data && (p.inlineData.mimeType || "").includes("audio/pcm")) {
        this._setState("speaking");
        this._playPcmChunk(p.inlineData.data);
      }
    }
    if (sc.turnComplete && this.state !== "interrupted") {
      setTimeout(() => this._setState("listening"), 200);
    }
  }
}
